// The rules behind the operator week calendar (#3039, M12): which week is
// shown, which hour cell is which slot, and when a picked range may be booked.
//
// Pure and separate from the component because each of these has a rule that
// is easy to get subtly wrong — a week anchored in the browser's zone, a
// fall-back day with two 02:00 hours, a range that crosses a booked hour.
//
// The calendar reads the occupancy endpoint, which is derived from the booking
// guard rather than from either availability endpoint
// (docs/reviews/availability-computation-divergence.md). A slot shown free here
// is free by the same rule the write path enforces.

import { GroupOccupancyResponse, OccupancyDay } from '../../../api/group/occupancyApiSlice';
import { GRID_TZ, zonedDayStart } from '../components/utilizationRange';
import { stationCalendarDay, zonedFormatter } from '../../../utils/stationTime';

/** One hour per cell: the room booking screens take whole hours. */
export const WEEK_SLOT_MINUTES = 60;

const DAYS_IN_WEEK = 7;

const WEEKDAY_OPTIONS: Intl.DateTimeFormatOptions = { weekday: 'short' };

const WEEKDAY_INDEX: Record<string, number> = {
    Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6,
};

// Every helper below takes the zone the week is read in: the calendar's own
// frame (the week it names, its navigation) is the station default, and each
// room's grid is its location's zone (#3219).

/** `YYYY-MM-DD` of an instant, in `timeZone` (the station's by default) — the
 * shared `stationCalendarDay`, kept under this name for its callers. */
export const zonedDateKey = (instant: Date, timeZone: string = GRID_TZ): string =>
    stationCalendarDay(instant, timeZone);

/**
 * Monday 00:00 of the week to show, in `timeZone`.
 *
 * `param` is the `week` query parameter (`YYYY-MM-DD`, any day of the week);
 * absent or unreadable, the current week is shown. Read at noon UTC so the
 * calendar date it names is the same date in any zone within ±12 h of UTC.
 */
export const weekStartFor = (param: string | null, now: Date, timeZone: string = GRID_TZ): Date => {
    let anchor = now;
    if (param && /^\d{4}-\d{2}-\d{2}$/.test(param)) {
        const parsed = new Date(`${param}T12:00:00Z`);
        if (!Number.isNaN(parsed.getTime())) anchor = parsed;
    }
    const offset = WEEKDAY_INDEX[zonedFormatter('en-US', WEEKDAY_OPTIONS, timeZone).format(anchor)] ?? 0;
    return zonedDayStart(anchor, timeZone, -offset);
};

/** `weeks` whole weeks later (or earlier), still Monday 00:00 in `timeZone`. */
export const shiftWeek = (weekStart: Date, weeks: number, timeZone: string = GRID_TZ): Date =>
    zonedDayStart(weekStart, timeZone, weeks * DAYS_IN_WEEK);

/**
 * `[from, to)` for the week. Not capped at 7 × 24 h like the utilization
 * tabs: the backend allows 31 days, so a fall-back week's 169 hours fit, and
 * capping would drop Sunday's last hour.
 */
export const weekRange = (weekStart: Date, timeZone: string = GRID_TZ): { from: string, to: string } => ({
    from: weekStart.toISOString(),
    to: shiftWeek(weekStart, 1, timeZone).toISOString(),
});

/** One hour cell. Runs from `from` to `to`. */
export interface WeekSlot {
    from: string;
    to: string;
    used: number;
    free: number;
    reserved: boolean;
}

/**
 * Every slot of the response in time order, each with its end — the next
 * cell's start, or the range end for the last one (the endpoint's contract:
 * "the slot runs from `from` to the next cell's `from`").
 */
export const flattenSlots = (response: Pick<GroupOccupancyResponse, 'days' | 'to'>): WeekSlot[] => {
    const cells = response.days.flatMap((day) => day.cells);
    return cells.map((cell, index) => ({
        from: cell.from,
        to: cells[index + 1]?.from ?? response.to,
        used: cell.used,
        free: cell.free,
        reserved: cell.hasReserved === true,
    }));
};

/**
 * Can a booking start in this slot? It has to have a free unit, and it has
 * to start now or later — the create screens do not offer past start times.
 */
export const isBookable = (slot: WeekSlot, now: Date): boolean =>
    slot.free > 0 && new Date(slot.from).getTime() >= now.getTime();

/**
 * The booking range between two picked slots, or `null` when any slot in
 * between cannot be booked. Order-independent. Checked over EVERY slot, not
 * just the drawn hour columns: a range across midnight runs through night
 * hours the grid may not show.
 */
export const selectionRange = (
    slots: WeekSlot[],
    firstFrom: string,
    secondFrom: string,
    now: Date,
): { from: string, to: string } | null => {
    const a = slots.findIndex((slot) => slot.from === firstFrom);
    const b = slots.findIndex((slot) => slot.from === secondFrom);
    if (a < 0 || b < 0) return null;
    const [start, end] = a <= b ? [a, b] : [b, a];
    const range = slots.slice(start, end + 1);
    if (!range.every((slot) => isBookable(slot, now))) return null;
    return { from: range[0]!.from, to: range[range.length - 1]!.to };
};

/**
 * One day's slots keyed by hour of day, in the zone `hourOf` reads (the room's).
 *
 * On the October fall-back day two cells carry the same hour; they are merged
 * into one cell spanning both, taken as soon as either is — a cell must never
 * read free for an hour that is partly booked (utilizationHours.ts, rule 1).
 * On the March spring-forward day one hour simply has no cell.
 */
export const daySlotsByHour = (
    day: OccupancyDay,
    slots: WeekSlot[],
    hourOf: (iso: string) => number,
): Map<number, WeekSlot> => {
    const byFrom = new Map(slots.map((slot) => [slot.from, slot]));
    const result = new Map<number, WeekSlot>();
    day.cells.forEach((cell) => {
        const slot = byFrom.get(cell.from);
        if (!slot) return;
        const hour = hourOf(slot.from);
        const existing = result.get(hour);
        if (!existing) {
            result.set(hour, slot);
            return;
        }
        result.set(hour, {
            from: existing.from,
            to: slot.to,
            used: Math.max(existing.used, slot.used),
            free: Math.min(existing.free, slot.free),
            reserved: existing.reserved || slot.reserved,
        });
    });
    return result;
};
